import { useEffect } from "react";

/**
 * UI polish only: the CopilotKit launcher FAB ships with a correct `aria-label`
 * ("Open/Close OneThing assistant") but no `title`, so sighted users get no
 * hover tooltip telling them what the floating bubble is. CopilotKit owns the
 * button DOM and exposes no tooltip prop (only the aria-label keys), so we
 * mirror the live aria-label onto a `title` attribute.
 *
 * Purely additive — sets one HTML attribute, changes no behaviour. A
 * MutationObserver keeps `title` in sync when the FAB's aria-label flips on
 * open/close. Renders nothing.
 */
export function CopilotLauncherTooltip() {
  useEffect(() => {
    const sync = (btn: HTMLButtonElement) => {
      const label = btn.getAttribute("aria-label");
      if (label && btn.getAttribute("title") !== label) btn.setAttribute("title", label);
    };

    const find = () =>
      document.querySelector<HTMLButtonElement>(
        'button.copilotKitButton[aria-label*="OneThing assistant"]',
      );

    // The FAB mounts a tick after the provider; poll briefly until it exists.
    let observer: MutationObserver | null = null;
    const attach = (btn: HTMLButtonElement) => {
      sync(btn);
      observer = new MutationObserver(() => sync(btn));
      observer.observe(btn, { attributes: true, attributeFilter: ["aria-label"] });
    };

    const existing = find();
    if (existing) {
      attach(existing);
    } else {
      const docObserver = new MutationObserver(() => {
        const btn = find();
        if (btn) {
          docObserver.disconnect();
          attach(btn);
        }
      });
      docObserver.observe(document.body, { childList: true, subtree: true });
      return () => {
        docObserver.disconnect();
        observer?.disconnect();
      };
    }

    return () => observer?.disconnect();
  }, []);

  return null;
}
